import { Apis } from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    payload?.totalCount ??
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return {
    items,
    pagination,
    totalCount,
    category_count: payload?.category_count,
    payload,
  };
};

// --- Interior designs ---
export const useCorporateInteriorDesign = () => {
  const getCorporateInteriorDesign = async (
    setCorporateInteriorDesignData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = "",
    category_name = "",
  ) => {
    try {
      const params = {};
      if (searchFilter) {
        params.searchFilter = searchFilter;
      }
      if (category_name) {
        params.category_name = category_name;
      }
      const response = await Apis.getCorporateInteriorDesign(offset, limit, params);
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      setCorporateInteriorDesignData(items);
      setTotal(totalCount);
      return { data: items, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error fetching corporate interior design:", error);
      throw new Error(getApiError(error, "Failed to fetch corporate interior designs."));
    }
  };

  const createCorporateInteriorDesign = async (data) => {
    try {
      const response = await Apis.createCorporateInteriorDesign(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error creating corporate interior design:", error);
      throw error;
    }
  };

  const updateCorporateInteriorDesign = async (id, data) => {
    try {
      const response = await Apis.updateCorporateInteriorDesign(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating corporate interior design:", error);
      throw error;
    }
  };

  const deleteCorporateInteriorDesign = async (id) => {
    try {
      const response = await Apis.deleteCorporateInteriorDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting corporate interior design:", error);
      throw error;
    }
  };

  const getCorporateInteriorDesignById = async (id, setInteriorData) => {
    try {
      const response = await Apis.getCorporateInteriorDesignById(id);
      const design = unwrapResponseBody(response?.data);
      if (typeof setInteriorData === "function") setInteriorData(design);
      return design;
    } catch (error) {
      console.error("Error fetching corporate interior design by id:", error);
      throw error;
    }
  };

  const showCorporateInteriorDesignOnHomeScreen = async (corporateInteriorDesignId, homeScreen) => {
    try {
      const data = {
        corporateInteriorDesignId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addCorporateInteriorDesignToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateCorporateInteriorDesignPosition = async (corporateInteriorDesignId, newPosition) => {
    try {
      const response = await Apis.updateCorporateInteriorDesignPosition({
        corporateInteriorDesignId,
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating corporate interior design position:", error);
      throw error;
    }
  };

  return {
    getCorporateInteriorDesign,
    createCorporateInteriorDesign,
    updateCorporateInteriorDesign,
    deleteCorporateInteriorDesign,
    getCorporateInteriorDesignById,
    showCorporateInteriorDesignOnHomeScreen,
    updateCorporateInteriorDesignPosition,
  };
};

// --- Interior design categories ---
export const useCorporateInteriorDesignCategory = () => {
  const addCorporateInteriorDesignCategory = async (categoryData) => {
    try {
      const response = await Apis.addCorporateInteriorDesignCategory(categoryData);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to add category.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const getCorporateInteriorDesignCategories = async () => {
    try {
      const response = await Apis.getCorporateInteriorDesignCategories();
      const { items, pagination, payload } = unwrapListPayload(response?.data);
      return { data: items, pagination, ...payload };
    } catch (error) {
      throw error;
    }
  };

  const getCorporateInteriorDesignCategoriesWithPagination = async (
    offset = 0,
    limit = 10,
    name = "",
    status = "",
  ) => {
    try {
      const params = {};
      if (name) {
        params.name = name;
      }
      if (status !== "") {
        params.status =
          status === "true" ? true : status === "false" ? false : status;
      }
      const response = await Apis.getCorporateInteriorDesignCategoriesWithPagination(
        offset,
        limit,
        params,
      );
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      return {
        data: items,
        pagination,
        totalCount,
        ...payload,
      };
    } catch (error) {
      throw error;
    }
  };

  const updateCorporateInteriorDesignCategory = async (id, categoryData) => {
    try {
      const response = await Apis.updateCorporateInteriorDesignCategory(id, categoryData);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update category.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const deleteCorporateInteriorDesignCategory = async (categoryId) => {
    try {
      const response = await Apis.deleteCorporateInteriorDesignCategory(categoryId);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to delete category.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const toggleCorporateInteriorDesignCategoryStatus = async (categoryId, status) => {
    try {
      const response = await Apis.toggleCorporateInteriorDesignCategoryStatus(
        categoryId,
        status,
      );
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to toggle category status.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  const getAllCorporateInteriorDesignCategories = async () => {
    try {
      const response = await Apis.getAllCorporateInteriorDesignCategories();
      const { items } = unwrapListPayload(response?.data);
      return items;
    } catch (error) {
      throw error;
    }
  };

  const updateCorporateInteriorDesignCategoryPosition = async (
    categoryId,
    newPosition,
  ) => {
    try {
      const response = await Apis.updateCorporateInteriorDesignCategoryPosition({
        categoryId,
        newPosition,
      });
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update category position.");
      }
      return body;
    } catch (error) {
      throw error;
    }
  };

  return {
    addCorporateInteriorDesignCategory,
    getCorporateInteriorDesignCategories,
    getCorporateInteriorDesignCategoriesWithPagination,
    updateCorporateInteriorDesignCategory,
    deleteCorporateInteriorDesignCategory,
    toggleCorporateInteriorDesignCategoryStatus,
    getAllCorporateInteriorDesignCategories,
    updateCorporateInteriorDesignCategoryPosition,
  };
};
